// "Use my theme's colours" for the four PDP widgets (Product Bundle, Cross-sell,
// Upsell, Free Gift). The storefront derives the widget palette from the theme at
// runtime when the widget settings say so; a merchant who picked their own colours
// keeps them. This module is the ONE place that holds the contract shared with the
// storefront (theme-app-extension widgets) and Prime-Backend:
//
//   Theme mode is ON when `UseThemeColors === true`, OR when `UseThemeColors` is
//   undefined (saved before the flag existed) AND every colour key present in the
//   saved settings equals the OLD shipped default for that widget (hex compared
//   case-insensitively). A missing or empty colour key counts as "not customised".
//
// The OLD shipped defaults below are the colour values Prime-Backend's
// shop.service.ts activateDefaultWidgets() provisioned on install (bundle, cross-sell,
// upsell and gift blocks). They are frozen on purpose: even if the current defaults
// in config/defaultWidgetSettings.js or adaptors/shop/shop-info.js change later, the
// detection must keep recognising what older shops actually have stored.

export const OLD_DEFAULT_WIDGET_COLORS = {
  bundle: {
    WidgetBackgroundColor: '#F7F7F7',
    HeaderBackgroundColor: '#CDFEE1',
    TitleColor: '#303030',
    SubTitleColor: '#616161',
    TextColor: '#303030',
    GiftBorderColor: '#A8D8FF',
    GiftBackgroundColor: '#EAF4FF',
    ProgressBarColor: '#45946D',
    ProgressBackgroundColor: '#D9F0E4',
    ButtonBackgroundColor: '#303030',
    ButtonTextColor: '#FFFFFF',
  },
  crossSell: {
    ButtonBackgroundColor: '#29845A',
    ButtonTextColor: '#ffffff',
    WidgetBackgroundColor: '#F7F7F7',
    TextColor: '#303030',
  },
  upsell: {
    ButtonBackgroundColor: '#29845A',
    ButtonTextColor: '#ffffff',
    WidgetBackgroundColor: '#F7F7F7',
    TextColor: '#303030',
  },
  gift: {
    WidgetBackgroundColor: '#FFFFFF',
    TextColor: '#222222',
    TitleColor: '#111111',
    ProgressBarColor: '#111111',
    ProgressBackgroundColor: '#E6E6E6',
    ButtonBackgroundColor: '#111111',
    ButtonTextColor: '#FFFFFF',
  },
};

// Neutral palette the admin preview renders with while theme mode is on (the admin
// cannot know the merchant's theme; the storefront resolves the real colours).
export const THEME_PREVIEW_COLORS = {
  card: '#FFFFFF',
  text: '#1A1A1A',
  muted: '#616161',
  accent: '#111111',
  buttonText: '#FFFFFF',
  track: '#E6E6E6',
  border: 'rgba(0,0,0,.12)',
};

const P = THEME_PREVIEW_COLORS;

// The neutral palette mapped onto each widget's colour keys, so a preview can be fed
// `{ ...settings, ...THEME_PREVIEW_PALETTE[widget] }` without changing its own code.
export const THEME_PREVIEW_PALETTE = {
  bundle: {
    WidgetBackgroundColor: P.card,
    HeaderBackgroundColor: P.card,
    TitleColor: P.text,
    SubTitleColor: P.muted,
    TextColor: P.text,
    GiftBorderColor: P.border,
    GiftBackgroundColor: P.card,
    ProgressBarColor: P.accent,
    ProgressBackgroundColor: P.track,
    ButtonBackgroundColor: P.accent,
    ButtonTextColor: P.buttonText,
  },
  crossSell: {
    WidgetBackgroundColor: P.card,
    TextColor: P.text,
    ButtonBackgroundColor: P.accent,
    ButtonTextColor: P.buttonText,
  },
  upsell: {
    WidgetBackgroundColor: P.card,
    TextColor: P.text,
    ButtonBackgroundColor: P.accent,
    ButtonTextColor: P.buttonText,
  },
  gift: {
    WidgetBackgroundColor: P.card,
    TextColor: P.text,
    TitleColor: P.text,
    ProgressBarColor: P.accent,
    ProgressBackgroundColor: P.track,
    ButtonBackgroundColor: P.accent,
    ButtonTextColor: P.buttonText,
  },
};

const normalizeColor = (value) => String(value).trim().toLowerCase();

/**
 * True when every colour key of `widgetKey` that is present in `settings` still holds
 * the OLD shipped default (case-insensitive). Missing / empty keys do not count as
 * customised, so an object with no colour keys at all is "all defaults".
 */
export function isOldDefaultPalette(widgetKey, settings) {
  const table = OLD_DEFAULT_WIDGET_COLORS[widgetKey];
  if (!table) return false;
  if (!settings) return true;
  return Object.keys(table).every((key) => {
    const value = settings[key];
    if (value === undefined || value === null || value === '') return true;
    return normalizeColor(value) === normalizeColor(table[key]);
  });
}

/**
 * The theme-mode state the storefront will use for these settings: the explicit flag
 * when saved, else the old-default detection (older shop).
 */
export function resolveUseThemeColors(widgetKey, settings) {
  if (typeof settings?.UseThemeColors === 'boolean') return settings.UseThemeColors;
  return isOldDefaultPalette(widgetKey, settings);
}

/** `settings` with an explicit `UseThemeColors`, so the next save persists the detected state. */
export function withUseThemeColors(widgetKey, settings) {
  return { ...settings, UseThemeColors: resolveUseThemeColors(widgetKey, settings) };
}

/** Settings to feed the admin preview: the neutral palette while theme mode is on, else as saved. */
export function previewColorSettings(widgetKey, settings) {
  if (!settings || !settings.UseThemeColors) return settings;
  return { ...settings, ...THEME_PREVIEW_PALETTE[widgetKey] };
}
